import { createApi } from "@reduxjs/toolkit/query/react";
import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import type { IApiResponse } from "@/types";
import type {
  IOffboarding,
  IOffboardingInvitation,
  IOffboardingDocument,
  IOffboardingSettlement,
  IOffboardingApprover,
  IGratuityPreview,
  IStatutoryPreview,
} from "@/types/(company)/offboarding";
import {
  API_MS_OFFBOARDING_MY,
  API_MS_OFFBOARDING_MY_INVITATION,
  API_MS_OFFBOARDING_ACCEPT_INVITATION,
  API_MS_OFFBOARDING_MY_DOCUMENTS,
  API_MS_OFFBOARDING_MY_SETTLEMENT,
  API_MS_OFFBOARDING_MY_GRATUITY_PREVIEW,
  API_MS_OFFBOARDING_MY_STATUTORY_PREVIEW,
  API_MS_OFFBOARDING_APPROVAL_STATUS,
} from "@/constants/apiEndpoints";

export const msOffboardingApi = createApi({
  reducerPath: "msOffboardingApi",
  baseQuery: dynamicBaseQuery,
  refetchOnMountOrArgChange: true,
  tagTypes: [
    "msOffboarding",
    "msOffboardingInvitation",
    "msOffboardingDocuments",
    "msOffboardingSettlement",
    "msOffboardingApprovalStatus",
  ],
  endpoints: (builder) => ({
    msGetMyOffboarding: builder.query<IApiResponse<IOffboarding>, void>({
      query: () => ({ url: API_MS_OFFBOARDING_MY }),
      providesTags: ["msOffboarding"],
    }),

    msGetMyInvitation: builder.query<IApiResponse<IOffboardingInvitation>, void>({
      query: () => ({ url: API_MS_OFFBOARDING_MY_INVITATION }),
      providesTags: ["msOffboardingInvitation"],
    }),

    msAcceptMyInvitation: builder.mutation<IApiResponse<IOffboardingInvitation>, void>({
      query: () => ({ url: API_MS_OFFBOARDING_ACCEPT_INVITATION, method: "PATCH" }),
      invalidatesTags: ["msOffboardingInvitation", "msOffboarding"],
    }),

    msGetMyDocuments: builder.query<IApiResponse<IOffboardingDocument[]>, void>({
      query: () => ({ url: API_MS_OFFBOARDING_MY_DOCUMENTS }),
      providesTags: ["msOffboardingDocuments"],
    }),

    msGetMySettlement: builder.query<IApiResponse<IOffboardingSettlement>, void>({
      query: () => ({ url: API_MS_OFFBOARDING_MY_SETTLEMENT }),
      providesTags: ["msOffboardingSettlement"],
    }),

    msGetMyGratuityPreview: builder.query<IApiResponse<IGratuityPreview>, void>({
      query: () => ({ url: API_MS_OFFBOARDING_MY_GRATUITY_PREVIEW }),
    }),

    msGetMyStatutoryPreview: builder.query<IApiResponse<IStatutoryPreview>, void>({
      query: () => ({ url: API_MS_OFFBOARDING_MY_STATUTORY_PREVIEW }),
    }),

    msGetMyOffboardingApprovalStatus: builder.query<IApiResponse<IOffboardingApprover[]>, void>({
      query: () => ({ url: API_MS_OFFBOARDING_APPROVAL_STATUS }),
      providesTags: ["msOffboardingApprovalStatus"],
    }),
  }),
});

export const {
  useMsGetMyOffboardingQuery,
  useMsGetMyInvitationQuery,
  useMsAcceptMyInvitationMutation,
  useMsGetMyDocumentsQuery,
  useMsGetMySettlementQuery,
  useMsGetMyGratuityPreviewQuery,
  useMsGetMyStatutoryPreviewQuery,
  useMsGetMyOffboardingApprovalStatusQuery,
} = msOffboardingApi;
